import {Router} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {Tour} from '@app/common/interfaces/tour';
import {TOURS} from "@app/common/constants/tours";
import {LngKeys} from "@app/common/enums/lng-keys";
import {TranslateService} from "@ngx-translate/core";
import {Quotes} from '@app/common/interfaces/quotes';
import {Meta, Title} from "@angular/platform-browser";
import {inject, Injectable, Renderer2} from '@angular/core';
import {updateCanonicalURL} from "@app/common/helpers/update-canonical";
import {Route_seo, Routes_seo, SEO_Config} from "@app/common/interfaces/route-paths";
import {LANGUAGES_OBJ, route_paths, routes_seo} from "@app/common/constants/languages_data";

type LangKey = keyof typeof route_paths;
type RouteKey = keyof typeof route_paths['en'];

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private readonly router = inject(Router);
  private readonly metaService = inject(Meta);
  private readonly titleService = inject(Title);
  private readonly document = inject(DOCUMENT);
  private readonly translateService = inject(TranslateService);

  public readonly route_paths = route_paths;
  public languages = structuredClone(Object.values(LANGUAGES_OBJ));
  public selectedLanguage = this.languages[0];

  public tours: Tour[] = structuredClone(TOURS).filter((tour: Tour) => !tour.disabled);
  public quotes: Quotes | null = null;

  public dateTime = new Date().getTime();

  private readonly baseUrl = 'https://www.tourdearmenia.com';

  constructor() {
  }

  public addMetaTags(project: SEO_Config) {
    /* Update meta tags */
    this.metaService.updateTag({name: 'keywords', content: project.keywords});

    /* Schema.org markup for Google+ */
    /** Interops are duplicated, not replaces (Bug) */
    // this.metaService.updateTag({itemprop: 'name', content: title});
    // this.metaService.updateTag({itemprop: 'description', content: desc});
    // this.metaService.updateTag({itemprop: 'image', content: imgUrl});

    /*  Open Graph data  */
    this.metaService.updateTag({property: 'og:locale', content: project.og.local});
    this.metaService.updateTag({property: 'og:image:alt', content: 'selfcv'});
  }

  public setHtmlLangAttribute(lang: string, renderer: Renderer2) {
    // Update the <html> tag's lang attribute
    renderer.setAttribute(this.document.documentElement, 'lang', lang);

    /*if (lang === LngKeys.AR) {
      this.setDirection('rtl', renderer);
      loadCSS(this.document, 'https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&display=swap');
    } else {
      this.removeDirection(renderer);
    }*/

    this.removeDirection(renderer);
  }

  private setDirection(dir: 'ltr' | 'rtl', renderer: Renderer2) {
    const html = this.document.documentElement;
    renderer.setAttribute(html, 'dir', dir);
  }

  private removeDirection(renderer: Renderer2) {
    const html = this.document.documentElement;
    renderer.removeAttribute(html, 'dir');
  }

  public setLng(key: LngKeys) {
    const lngExist = this.languages.some((lng) => lng.key === key);
    const lang = lngExist ? key : LngKeys.EN;

    this.selectedLanguage = this.languages.find((lng) => lng.key === key) || this.languages[0];
    this.translateService.use(lang);
  }

  public setPageSeo(page: string, renderer: Renderer2) {
    const currentLang = this.translateService.currentLang as keyof Routes_seo || LngKeys.EN;
    const route_seo = routes_seo[currentLang][page as keyof Route_seo];
    const title = route_seo.title;
    const desc = route_seo.description;

    const url = `https://www.selfcv.com${this.router.url}`;
    const seoImgPath = 'https://www.selfcv.com/assets/img/seo-images';
    let ogImgUrl = `${seoImgPath}/home-page/home-page-${currentLang}.png`;

    const existedSeoImgPages = ['cv_templates', 'cover_letters'];

    if (existedSeoImgPages.includes(page)) {
      ogImgUrl = `${seoImgPath}/${page}/${page}-page-${currentLang}.png`;
    }

    // Title
    this.titleService.setTitle(title);
    this.metaService.updateTag({property: 'og:title', content: title});
    this.metaService.updateTag({name: 'twitter:title', content: title});

    // Description
    this.metaService.updateTag({name: 'description', content: desc});
    this.metaService.updateTag({property: 'og:description', content: desc});
    this.metaService.updateTag({name: 'twitter:description', content: desc});

    // Image
    // this.metaService.updateTag({interop: 'image', content: ogImgUrl});
    this.metaService.updateTag({property: 'og:image', content: ogImgUrl});
    this.metaService.updateTag({name: 'twitter:image:src', content: ogImgUrl});
    this.metaService.updateTag({name: 'msapplication-TileImage', content: ogImgUrl});

    // Url
    this.metaService.updateTag({property: 'og:url', content: url});
    this.metaService.updateTag({name: 'twitter:site', content: url});
    this.metaService.updateTag({name: 'twitter:url', content: url});

    updateCanonicalURL(url, this.document, renderer);

    this.setHreflangTags(renderer);

    // Itemprop
    this.updateCustomMetaTag('itemprop', 'name', title);
    this.updateCustomMetaTag('itemprop', 'image', ogImgUrl);
    this.updateCustomMetaTag('itemprop', 'description', desc);
  }

  private updateCustomMetaTag(attrName: string, attrValue: string, content: string) {
    const head = this.document.getElementsByTagName('head')[0];
    const existingTag = head.querySelector(`meta[${attrName}="${attrValue}"]`);

    if (existingTag) {
      existingTag.setAttribute('content', content);
    } else {
      const meta = this.document.createElement('meta');
      meta.setAttribute(attrName, attrValue);
      meta.setAttribute('content', content);
      head.appendChild(meta);
    }
  }

  public setHreflangTags(renderer: Renderer2) {
    const currentUrl = this.router.url;

    // 1. Find route key (e.g. "cv_templates")
    const routeKey = this.findRouteKeyByUrl(currentUrl);

    if (!routeKey) {
      console.warn('Route key not found for:', currentUrl);
      return;
    }

    // 2. Remove existing hreflang tags
    const existingTags = this.document.querySelectorAll("link[rel='alternate'][hreflang]");
    existingTags.forEach(tag => tag.remove());

    // 3. Generate hreflangs for all languages
    this.languages.forEach(language => {
      const lang = language.key as LangKey;

      const path = route_paths[lang][routeKey];

      if (!path) {
        return;
      }

      let fullUrl = `${this.baseUrl}${path}`;

      if (!fullUrl.endsWith('/')) {
        fullUrl = `${fullUrl}/`;
      }

      const link = renderer.createElement('link');
      renderer.setAttribute(link, 'rel', 'alternate');
      renderer.setAttribute(link, 'hreflang', lang);
      renderer.setAttribute(link, 'href', fullUrl);

      renderer.appendChild(this.document.head, link);
    });

    // 4. x-default (usually EN or main version)
    const defaultPath = route_paths['en'][routeKey];
    let xDefaultUrl = `${this.baseUrl}${defaultPath}`;

    if (!xDefaultUrl.endsWith('/')) {
      xDefaultUrl = `${xDefaultUrl}/`;
    }

    const xDefaultLink = renderer.createElement('link');
    renderer.setAttribute(xDefaultLink, 'rel', 'alternate');
    renderer.setAttribute(xDefaultLink, 'hreflang', 'x-default');
    renderer.setAttribute(xDefaultLink, 'href', xDefaultUrl);

    renderer.appendChild(this.document.head, xDefaultLink);
  }

  private findRouteKeyByUrl(url: string): RouteKey | null {
    const cleanUrl = this.normalizeUrl(url);

    for (const lang of Object.keys(route_paths) as LangKey[]) {
      const routes = route_paths[lang];

      for (const key in routes) {
        const routeKey = key as RouteKey;

        if (this.normalizeUrl(routes[routeKey]) === cleanUrl) {
          return routeKey;
        }
      }
    }

    return null;
  }

  private normalizeUrl(url: string): string {
    return url.replace(/\/+$/, '') || '/';
  }

}
